import { useRef, useState } from "react";
import {
  Circle,
  CircleDot,
  CheckCircle2,
  AlertCircle,
  Pause,
  XCircle,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  type ActionItem,
  type ActionState,
  nextStateInCycle,
} from "@/hooks/use-action-items";

/**
 * BW19 PR-E + HOTFIX-BW20.1 PR-C — single task card used by the list view,
 * kanban, /today and project detail.
 *
 * Affordances:
 *   - Tick (state icon button on the left): single-step cycle through
 *     open → in_progress → done. Tick = "I did it".
 *   - Desktop hover: a small ghost X icon appears at the right edge.
 *     Click → state='cancelled'. X = "Doesn't apply anymore".
 *   - Mobile swipe: touch-drag the card left to reveal a red Delete
 *     button (iOS Mail / Things style). Tap Delete → state='cancelled'.
 *
 * Both done and cancelled hide from Today's list. Both visible in /tasks
 * when "Show done" / "Show cancelled" filters are on.
 */

const STATE_ICON: Record<ActionState, LucideIcon> = {
  open: Circle,
  in_progress: CircleDot,
  blocked: AlertCircle,
  done: CheckCircle2,
  cancelled: XCircle,
  deferred: Pause,
};

const STATE_LABEL: Record<ActionState, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
  deferred: "Deferred",
};

const SWIPE_REVEAL_PX = 64;
const SWIPE_THRESHOLD_PX = 32;

export function TaskCard({
  item,
  onCycleState,
  onCancel,
  onOpen,
}: {
  item: ActionItem;
  onCycleState: (next: ActionState) => void;
  /**
   * BW20.1 PR-C: explicit cancel action. When undefined the cancel
   * affordances (hover X, swipe-to-reveal) are not rendered — useful for
   * read-only contexts.
   */
  onCancel?: () => void;
  onOpen: () => void;
}) {
  const StateIcon = STATE_ICON[item.state];
  const overdue =
    item.due_at != null &&
    new Date(item.due_at) < new Date() &&
    item.state !== "done" &&
    item.state !== "cancelled";

  const [hover, setHover] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const swipeRevealed = translateX <= -SWIPE_THRESHOLD_PX;

  function handleTouchStart(e: React.TouchEvent) {
    if (!onCancel) return;
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchMove(e: React.TouchEvent) {
    if (!onCancel || touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    // Only allow leftward drag (revealing the Delete button on the right).
    if (dx <= 0) {
      setTranslateX(Math.max(dx, -SWIPE_REVEAL_PX));
    }
  }
  function handleTouchEnd() {
    if (!onCancel) return;
    if (translateX <= -SWIPE_THRESHOLD_PX) {
      setTranslateX(-SWIPE_REVEAL_PX);
    } else {
      setTranslateX(0);
    }
    touchStartX.current = null;
  }

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "var(--radius-card)",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {onCancel && (
        <button
          type="button"
          aria-label="Cancel task — doesn't apply anymore"
          onClick={(e) => {
            e.stopPropagation();
            setTranslateX(0);
            onCancel();
          }}
          style={{
            position: "absolute",
            inset: 0,
            left: "auto",
            width: SWIPE_REVEAL_PX,
            background: "var(--brick, #b04a30)",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Delete
        </button>
      )}

      <article
        onClick={(e) => {
          if (swipeRevealed) {
            e.stopPropagation();
            setTranslateX(0);
            return;
          }
          onOpen();
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          padding: "var(--space-3)",
          background: "var(--bg-2)",
          border: `1px solid ${overdue ? "var(--danger, #c53030)" : "var(--hair)"}`,
          borderRadius: "var(--radius-card)",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          cursor: "pointer",
          position: "relative",
          transform: `translateX(${translateX}px)`,
          transition: touchStartX.current == null ? "transform 200ms ease-out" : "none",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCycleState(nextStateInCycle(item.state));
            }}
            aria-label={`Cycle state from ${STATE_LABEL[item.state]} — tick marks done`}
            title={`Tick to mark done (currently: ${STATE_LABEL[item.state]})`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--fg-2)",
              marginTop: 2,
            }}
          >
            <StateIcon size={16} />
          </button>
          <span
            style={{
              flex: 1,
              color: "var(--fg)",
              fontSize: 14,
              lineHeight: 1.4,
              textDecoration: item.state === "done" ? "line-through" : "none",
              opacity: item.state === "done" || item.state === "cancelled" ? 0.6 : 1,
            }}
          >
            {item.title}
          </span>
          {onCancel && hover && item.state !== "cancelled" && (
            <button
              type="button"
              aria-label="Cancel task — doesn't apply anymore"
              title="Doesn't apply anymore (sets cancelled)"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--fg-3)",
                cursor: "pointer",
                padding: 2,
                marginTop: 2,
                display: "inline-flex",
              }}
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          )}
        </header>
        <footer
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            fontSize: 12,
            color: "var(--fg-2)",
            paddingLeft: 24,
          }}
        >
          <span
            style={{
              padding: "2px 6px",
              background: "var(--chip)",
              borderRadius: 4,
            }}
          >
            {item.category}
          </span>
          {item.effort_estimate_min != null && (
            <span>~{item.effort_estimate_min}m</span>
          )}
          {item.due_at && (
            <span style={{ color: overdue ? "var(--danger, #c53030)" : "var(--fg-2)" }}>
              due {formatDate(item.due_at)}
            </span>
          )}
          {item.scheduled_at && <span>scheduled {formatDate(item.scheduled_at)}</span>}
          {item.blocker_reason && <span>blocked: {item.blocker_reason}</span>}
        </footer>
      </article>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}
